import { mPerLat, mPerLon } from './geo';
// cathodic protection (KKS) measuring points + pinpointed cable faults
import { haversine } from './model';
import { num } from './utility';

export const KKS_LIMIT = -850; // mV vs. Cu/CuSO4
export const IJ_ST = ['ok', 'bridged', 'defect'];
export const CAP_ST = ['ok', 'clogged', 'damaged', 'gas'];
export const mv = (v) => { const n = num(v); return n == null ? null : n > 0 ? -n : n; };
export const kksOk = (k) => (mv(k?.eoff) == null ? null : mv(k.eoff) <= KKS_LIMIT);
export const kksPoints = (project) => (project.fittings || []).filter((f) => f.type === 'kks');
export const kksHead = (t) => ['Nr.', t.catName, 'Eoff (mV)', 'Eon (mV)', t.kkResult, t.kkIj, t.kkCap, t.hyDate, 'Lat', 'Lon'];
export const kksRow = (f, t, i) => { const k = f.kks || {}, ok = kksOk(k); return [i + 1, f.name || '', mv(k.eoff) ?? '-', mv(k.eon) ?? '-', ok == null ? '-' : ok ? t.kkProt : t.kkUnprot, k.ij ? t['kkIj_' + k.ij] : '-', k.cap ? t['kkCap_' + k.cap] : '-', k.t ? new Date(k.t).toLocaleString(t.locale) : '', +f.lat.toFixed(7), +f.lon.toFixed(7)]; };

export const CF_TYPES = ['earth', 'short', 'sheath', 'joint', 'dig', 'fiber'];
export const CF_METHODS = ['thumper', 'step', 'tone', 'otdr'];
export const CF_VOLT = ['ns', 'ms', 'hs', 'tel'];
export const CF_PHASES = ['L1', 'L2', 'L3', 'N', 'PE', 'Schirm'];
export const CF_CABLES = ['NAYY', 'NYY', 'NA2XY', 'NA2XS2Y', 'N2XS2Y', 'NA2XS(F)2Y', 'A-DF(ZN)2Y (LWL)', 'A-DQ(ZN)B2Y (LWL)'];
export const cfPins = (project) => (project.fittings || []).filter((f) => f.type === 'cfault');
// distance of the pinpointed fault to the nearest pre-location pin
export const cfDev = (f, faults) => { const ps = (faults || []).flatMap((x) => x.pins || []); return ps.length ? Math.min(...ps.map((p) => haversine(p, [f.lat, f.lon]))) : null; };
export const cfText = (f, t, faults) => {
  const c = f.cf || {}, d = cfDev(f, faults);
  return [c.type && t['cfT_' + c.type], c.method && t['cfM_' + c.method], c.cable, c.volt && t['cfV_' + c.volt], (c.ph || []).length ? `${t.cfPhase}: ${c.ph.join(', ')}` : null, c.fiber ? `${t.cfFiber} ${c.fiber}` : null,
    num(c.riso) != null ? `Riso ${c.riso} ${c.unit || 'MΩ'}` : null, num(c.depth) != null ? `${t.cfDepth} ${c.depth} cm` : null, d != null ? `${t.cfDev} ${d.toFixed(1)} m` : null].filter(Boolean).join(' · ');
};

// line locating log (cable / pipe locator)
export const LOC_METHODS = ['galv', 'induct', 'clamp', 'passive', 'radio', 'sonde'];
export const locPoints = (project) => (project.fittings || []).filter((f) => f.type === 'locate');
const segD = (p, a, b) => {
  const k = mPerLon(p[0]), q = [(a[1] - p[1]) * k, (a[0] - p[0]) * mPerLat(p[0])], r = [(b[1] - p[1]) * k, (b[0] - p[0]) * mPerLat(p[0])];
  const dx = r[0] - q[0], dy = r[1] - q[1], l2 = dx * dx + dy * dy, u = l2 ? Math.max(0, Math.min(1, -(q[0] * dx + q[1] * dy) / l2)) : 0;
  return Math.hypot(q[0] + u * dx, q[1] + u * dy);
};
export function nearestPipe(pipes, fittings, pt, lls) {
  let best = null;
  (pipes || []).forEach((p) => { const ll = lls(p, fittings); for (let i = 1; i < ll.length; i++) { const d = segD(pt, ll[i - 1], ll[i]); if (!best || d < best.d) best = { pipe: p, d }; } });
  return best;
}
export const locText = (f, t) => { const l = f.loc || {}; return [l.method && t['loM_' + l.method], num(l.freq) != null && `${l.freq} kHz`, num(l.ma) != null && `${l.ma} mA`, num(l.depth) != null && `${t.cfDepth} ${l.depth} cm`, l.tape != null && `${t.loTape}: ${l.tape ? `${t.loYes}${num(l.tapeD) != null ? ` (${l.tapeD} cm)` : ''}` : t.loNo}`].filter(Boolean).join(' · '); };

// scaffold rental timer
export const DAY = 86400000;
export function scafState(s, now = Date.now()) {
  const st = s.start ? new Date(s.start).getTime() : null, wk = num(s.weeks);
  if (st == null || wk == null) return { st: 'none' };
  const due = st + wk * 7 * DAY, end = s.end ? new Date(s.end).getTime() : null, ref = end ?? now;
  const left = Math.ceil((due - ref) / DAY), over = Math.max(0, -left), extraW = Math.ceil(over / 7), extra = extraW * (num(s.price) || 0);
  return { st: end ? (over ? 'doneOver' : 'done') : left < 0 ? 'red' : left <= 7 ? 'yellow' : 'green', due, left, over, extraW, extra, base: wk * (num(s.price) || 0) };
}

// KKS history: trend of Eoff (more negative = better protection)
export const kksHist = (k) => [...(k?.hist || [])].sort((a, b) => a.t - b.t);
export function kksTrend(k) {
  const h = kksHist(k).filter((x) => mv(x.eoff) != null);
  if (h.length < 2) return null;
  const d = mv(h[h.length - 1].eoff) - mv(h[h.length - 2].eoff);
  return Math.abs(d) < 5 ? 'flat' : d > 0 ? 'down' : 'up';
}
// chart geometry shared by sheet (SVG) and PDF
export function kksChart(k, w, h) {
  const pts = kksHist(k).filter((x) => mv(x.eoff) != null);
  if (!pts.length) return null;
  const vals = pts.flatMap((x) => [mv(x.eoff), mv(x.eon)].filter((v) => v != null)).concat(KKS_LIMIT);
  const lo = Math.min(...vals) - 30, hi = Math.max(...vals) + 30, t0 = pts[0].t, t1 = pts[pts.length - 1].t || t0 + 1;
  const X = (t) => (pts.length === 1 ? w / 2 : ((t - t0) / (t1 - t0 || 1)) * (w - 10) + 5), Y = (v) => ((hi - v) / (hi - lo)) * (h - 10) + 5;
  return { off: pts.map((x) => [X(x.t), Y(mv(x.eoff)), mv(x.eoff)]), on: pts.filter((x) => mv(x.eon) != null).map((x) => [X(x.t), Y(mv(x.eon))]), lim: Y(KKS_LIMIT), lo, hi };
}

// KKS yearly measuring plan: last measurement + interval (months) -> green / yellow (≤ 30 days) / red (overdue or never)
export const KKS_MONTHS_KEY = 'giss_kks_months';
export const kksMonths = () => Math.max(1, Math.min(60, Number(localStorage.getItem(KKS_MONTHS_KEY)) || 12));
export function kksDue(f, months = kksMonths(), now = Date.now()) {
  const k = f.kks || {}, h = kksHist(k);
  const last = h.length ? h[h.length - 1].t : mv(k.eoff) != null ? k.t : null;
  if (!last) return { st: 'red', last: null, due: null, left: null };
  const d = new Date(last); d.setMonth(d.getMonth() + months);
  const due = d.getTime(), left = Math.ceil((due - now) / DAY);
  return { st: left < 0 ? 'red' : left <= 30 ? 'yellow' : 'green', last, due, left };
}

// saved cross-bearing target (fitting type 'xbear')
export const xbPoints = (project) => (project.fittings || []).filter((f) => f.type === 'xbear' && f.xb);
export const xbText = (f, t) => { const x = f.xb, p = (q) => `${q[0].toFixed(6)}, ${q[1].toFixed(6)}`; return `P1 ${p(x.p1)} · ${x.b1}° | P2 ${p(x.p2)} · ${x.b2}° | ${t.xbDeclShort} ${x.decl || 0}° · ${t.xbAngle} ${Math.round(x.ang)}° · ${t.xbFrom} P1 ${Math.round(x.d1)} m / P2 ${Math.round(x.d2)} m`; };
